import {AttrsSchema, Component, ConfigOptions} from 'panel';
import copy from 'clipboard-copy';
import pick from 'lodash/pick';

import {promisifyModal} from 'common/components/modal/utils';
import {emitTrackEvent, ENTER_KEY_CODE} from 'common/util/dom';
import {userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {savedReportsLimitStore} from 'common/report/stores/saved-reports-limit';
import {FeatureGate, Permission, RestrictEntitySaving} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {EntityType} from 'common/report/util/saved-entities/types';
import {allowStaffOverride, getEntityTypeName} from 'common/report/util/saved-entities';
import {capitalize} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {
  CopyLinkUISource,
  eventPrefix,
  SharingTrackEvents,
  SharingTrackProperties,
} from 'common/widgets/sharing/tracking';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';
import {
  MAX_COHORT_DESCRIPTION_LENGTH,
  MAX_COHORT_NAME_LENGTH,
  MAX_DASHBOARD_DESCRIPTION_LENGTH,
  MAX_DASHBOARD_NAME_LENGTH,
  MAX_ENTITY_NAME_LENGTH,
  MAX_REPORT_DESCRIPTION_LENGTH,
} from 'common/constants';
import {useStore} from 'common/util/component-helpers';
import {sharingUtils} from 'common/widgets/sharing/utils';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';

import {template} from './template';
import css from './index.inline.styl';
import {
  CreateEntityModalAttrs,
  CreateEntityModalJsxProps,
  CreateEntityModalState,
  GlobalAccessType,
  ModalReturn,
  ModalValues,
} from './types';
import {canSaveNewReports, savedReportsLimitBannerRenderer} from './saved-reports-limit-utils';

const ENTITY_TYPE_TO_OPTIONS = {
  [EntityType.Cohort]: {
    maxNameLength: MAX_COHORT_NAME_LENGTH,
    maxDescriptionLength: MAX_COHORT_DESCRIPTION_LENGTH,
  },
  [EntityType.CustomProperty]: {},
  [EntityType.Metric]: {
    maxNameLength: 255,
  },
  [EntityType.DataGroup]: {
    featureFlag: null,
  },
  [EntityType.Report]: {
    maxNameLength: MAX_ENTITY_NAME_LENGTH,
    maxDescriptionLength: MAX_REPORT_DESCRIPTION_LENGTH,
  },
  [EntityType.Dashboard]: {
    maxNameLength: MAX_DASHBOARD_NAME_LENGTH,
    maxDescriptionLength: MAX_DASHBOARD_DESCRIPTION_LENGTH,
  },
  [EntityType.Workspace]: {
    maxNameLength: 255,
  },
  [EntityType.Journey]: {},
};

export class CreateEntityModal extends Component<CreateEntityModalState, CreateEntityModalAttrs> {
  asyncSave?: (value: ModalValues) => Promise<void>;
  private isSavedReportsLimitUpsellVisible = false;

  static get attrsSchema(): AttrsSchema<CreateEntityModalAttrs> {
    return {
      ...super.attrsSchema,
      'entity-type': {
        type: `string`,
        description: `Type of entity being created`,
        enum: Object.keys(ENTITY_TYPE_TO_OPTIONS),
      },
      'is-editing': {type: `boolean`, description: `Is this modal intended for editing an existing entity`},
      'initial-description': {type: `string`, description: `Initial description of entity`, default: ``},
      'initial-name': {type: `string`, description: `Initial name of entity`, default: ``},
      'modal-title': {type: `string`, description: `Title of modal`},
      open: {type: `boolean`},
      closeable: `boolean`,
      warning: {type: `string`},
      'visibility-controls-message': {type: `string`, description: `Message to display above visibility controls`},
      'project-id': {type: `number`},
      'show-share-management': `boolean`,
      'show-global-access-menu': `boolean`,
      'show-copy-link-button': `boolean`,
    };
  }

  get config(): ConfigOptions<CreateEntityModalState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        description: ``,
        existingEntityNames: new Set(),
        saving: null,
        name: ``,
        submitAttempted: false,
        open: false,
        globalAccessType: GlobalAccessType.OFF,
      },
      helpers: {
        limitBannerRenderer: () => {
          if (this.shouldCheckSavedReportsLimit()) {
            return savedReportsLimitBannerRenderer(savedReportsLimitStore, this.attr(`project-id`), `banner`);
          }
          return null;
        },
        getEntityTypeName,
        isSharingEnabled: () => {
          return currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing);
        },
        isSavingRestricted: () => {
          // saving restrictions are only handled in this modal for cohorts, other entity types restrict
          // saving before the user gets to this modal
          return (
            this.attr(`entity-type`) === EntityType.Cohort &&
            !currentProjectStore.value?.canSaveEntity(RestrictEntitySaving.Cohorts)
          );
        },
        openPricingUpsell: () => {
          emitTrackEvent(this, {
            eventName: `Click Upsell`,
            props: {
              'Feature Limit': FeatureGate.RestrictEntitySaving,
              'Entity Type': `cohorts`,
            },
          });
          window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
        },
        handleGlobalAccessTypeChange: (ev) => {
          this.update({
            globalAccessType: ev.detail.value,
          });
        },
        userLacksVisibilityPerms: () =>
          !userDetailsStore.value.projects[this.attr(`project-id`)]?.permissions.includes(
            Permission.EditCreatedEntityVisibility,
          ) && !allowStaffOverride(this.attr(`project-id`)),
        getGlobalAccessTypeSections: () => {
          return [
            {
              items: [
                {
                  label: GlobalAccessType.OFF,
                  value: GlobalAccessType.OFF,
                },
                {
                  label: GlobalAccessType.VIEWER,
                  value: GlobalAccessType.VIEWER,
                },
                {
                  label: GlobalAccessType.EDITOR,
                  value: GlobalAccessType.EDITOR,
                },
              ],
            },
          ];
        },
        showGlobalAccessMenu: () =>
          this.attr(`show-global-access-menu`) &&
          !this.attr(`is-editing`) &&
          !this.helpers.userLacksVisibilityPerms() &&
          this.helpers.isSharingEnabled(),
        getDescriptionInputAttrs: () => {
          const entityTypeName = getEntityTypeName(this.attr(`entity-type`) as EntityType);
          return {
            type: `textarea`,
            label: `Description`,
            necessary: `optional`,
            placeholder: `Enter a short description for your ${entityTypeName}`,
            ...(this.getDescriptionWarning()
              ? {'validation-status': `invalid`, 'error-message': this.getDescriptionWarning()}
              : {}),
            elref: `entityDescriptionInput`,
            disabled: this.helpers.isSavingRestricted(),
          };
        },
        getDataGroupDescriptionInputAttrs: () => {
          return {
            placeholder: `eg. Company`,
            label: `Display name`,
            necessary: `optional`,
            elref: `entityDescriptionInput`,
          };
        },
        setSubmitAttempted: () => {
          this.update({submitAttempted: true});
        },
        getNameError: () => {
          // start showing the error immediately if there's an initial value, otherwise wait for submit attempt
          return this.attr(`initial-name`).length > 0 || this.state.submitAttempted ? this.getNameError() : null;
        },
        hasSameNameAsExistingEntity: () => this.state.existingEntityNames.has(this.state.name.trim().toLowerCase()),
        handleDescriptionInput: (ev) => {
          this.update({description: ev.target.value});
        },
        handleModalChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.update({
              name: this.attr(`initial-name`),
              description: this.attr(`initial-description`),
              submitAttempted: false,
            });
            this.helpers.cancelChanges();
            this.dispatchEvent(new CustomEvent(`modalClosed`));
          }
        },
        handleSaveModalCancel: () => {
          this.helpers.cancelChanges(true);
        },
        cancelChanges: (clearOpenShareUponSave?: boolean) => {
          const {name, description} = this.state;
          this.dispatchEvent(new CustomEvent(`cancel`, {detail: {name, description, clearOpenShareUponSave}}));
        },
        saveChanges: (shareManagementButton?: boolean) => {
          const value = pick(this.state, [`name`, `description`, `globalAccessType`]);
          if (this.helpers.formIsValid()) {
            this.saveValue(value, shareManagementButton);
          }
        },
        handleNameInput: (ev) => {
          this.update({name: ev.target.value});
        },
        handleKeydown: (ev) => {
          if (ev.keyCode === ENTER_KEY_CODE) {
            ev.preventDefault();
            this.helpers.setSubmitAttempted();
            if (this.helpers.formIsValid()) {
              // save changes if enter
              this.helpers.saveChanges();
            }
          }
        },
        formIsValid: () => {
          const notLimited = this.shouldCheckSavedReportsLimit()
            ? canSaveNewReports(savedReportsLimitStore, this.attr(`project-id`))
            : true;
          return !this.getNameError() && !this.getDescriptionWarning() && notLimited;
        },
        copyLink: async () => {
          try {
            let shortUrl = window.location.toString();
            // There is an issue with waiting for a short link generation, and triggering copy in non-chrome browsers.
            // ref SRFE-3236
            //@ts-ignore
            if (window.chrome !== null && window.navigator.vendor === `Google Inc.`) {
              shortUrl = await sharingUtils.createShortUrl(window.location.toString());
            }
            this.trackCopyLink();
            addToast({
              message: `URL copied!`,
            });
            await copy(shortUrl);
          } catch (err) {
            reporter.error(err, `Could not copy URL`);
            addToast({
              message: `Could not copy the URL`,
              type: ToastType.ERROR,
            });
          }
        },
      },
    };
  }

  set existingEntityNames(val: string[]) {
    this.update({existingEntityNames: new Set(val.map((name) => name.toLowerCase()))});
  }

  private elRefs: {[ref: string]: HTMLElement} = {};

  attributeChangedCallback(name: keyof CreateEntityModalAttrs, oldValue, newValue): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `initial-name`:
        this.update({name: newValue});
        break;
      case `initial-description`:
        this.update({description: newValue});
        break;
      case `open`:
        this.resetGlobalAccessType();
        this.trackSavedReportsLimitUpsellIfVisible();
        break;
    }
  }

  savedReportsLimitStoreCallback = () => {
    this.update();
    this.trackSavedReportsLimitUpsellIfVisible();
  };

  connectedCallback() {
    super.connectedCallback();
    useStore(this, userDetailsStore).fetch();
    useStore(this, currentProjectStore).fetch({projectId: this.attr(`project-id`)});
    useStore(this, savedReportsLimitStore, this.savedReportsLimitStoreCallback).fetch({
      projectId: this.attr(`project-id`),
    });
  }

  async saveValue(value, shareManagementButton?: boolean) {
    if (this.asyncSave) {
      this.dispatchEvent(new CustomEvent(`saving`, {detail: {value}}));
      const saving = this.asyncSave(value);
      this.update({saving});
      try {
        await saving;
      } catch (err) {
        this.dispatchEvent(new CustomEvent(`saveFailed`, {detail: {value, err}}));
        this.update({saving: null});
        return;
      }
    }

    const saveEventName = shareManagementButton ? `saveAndShare` : `save`;
    if (shareManagementButton) {
      this.trackSaveAndShare();
    }
    if (this.helpers.showGlobalAccessMenu()) {
      this.trackGlobalAccess();
    }
    this.dispatchEvent(new CustomEvent(saveEventName, {detail: {value}}));
  }

  setElRef(ref: string, el: HTMLElement) {
    this.elRefs[ref] = el;
  }

  getNameError() {
    const maxLength = ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].maxNameLength;
    const entityTypeName = getEntityTypeName(this.attr(`entity-type`) as EntityType, {article: `a`});
    if (this.helpers.hasSameNameAsExistingEntity()) {
      return `${capitalize(entityTypeName)} with that name already exists`;
    } else if (this.state.name.trim().length === 0) {
      return `Name is required for saving ${entityTypeName}`;
    } else if (maxLength && this.state.name.length > maxLength) {
      return this.lengthExceededWarning(`name`, maxLength, this.state.name.length);
    } else {
      return null;
    }
  }

  getDescriptionWarning() {
    const maxLength = ENTITY_TYPE_TO_OPTIONS[this.attr(`entity-type`)].maxDescriptionLength;
    if (maxLength && this.state.description.length > maxLength) {
      return this.lengthExceededWarning(`description`, maxLength, this.state.description.length);
    } else {
      return null;
    }
  }

  lengthExceededWarning(field: string, maxLength: number, currentLength: number) {
    return `${capitalize(field)} must be less than ${maxLength + 1} characters (${currentLength})`;
  }

  shouldCheckSavedReportsLimit(): boolean {
    return this.attr(`entity-type`) === EntityType.Report && !this.attr(`is-editing`);
  }

  resetGlobalAccessType() {
    this.update({
      globalAccessType: GlobalAccessType.OFF,
    });
  }

  trackSavedReportsLimitUpsellIfVisible(): void {
    const hasLimit = savedReportsLimitStore.value[this.attr(`project-id`)]?.hasFiniteLimit ?? false;
    const isSavedReportsLimitUpsellVisible = hasLimit && this.attr(`open`);

    if (isSavedReportsLimitUpsellVisible && !this.isSavedReportsLimitUpsellVisible) {
      trackUpsellEvent(`View Upsell`, `bookmark`);
    }

    this.isSavedReportsLimitUpsellVisible = isSavedReportsLimitUpsellVisible;
  }

  trackSaveAndShare() {
    emitTrackEvent(this, {
      eventName: SharingTrackEvents.SaveAndShare,
      eventPrefix,
      props: {
        [SharingTrackProperties.EntityType]: this.attr(`entity-type`),
      },
    });
  }

  trackGlobalAccess() {
    emitTrackEvent(this, {
      eventName: SharingTrackEvents.GlobalAccessSet,
      eventPrefix,
      props: {
        [SharingTrackProperties.GlobalAccessType]: this.state.globalAccessType,
      },
    });
  }

  trackCopyLink() {
    emitTrackEvent(this, {
      eventName: SharingTrackEvents.ShortLink,
      eventPrefix,
      props: {
        [SharingTrackProperties.CopyLinkUISource]: CopyLinkUISource.CreateEntityModal,
      },
    });
  }
}

export const CreateEntityModalEl = defineAndMakeJsxFactory<CreateEntityModalJsxProps>(
  `mp-create-entity-modal`,
  CreateEntityModal,
);

export const CreateEntityModalReact = panel2react(`mp-create-entity-modal`, CreateEntityModal);

export function createEntityModalPromiseReturn(closeEvent: Partial<CustomEvent>) {
  const isSaveType = closeEvent.type === `save` || closeEvent.type === `saveAndShare`;
  const values = isSaveType ? closeEvent.detail.value : closeEvent.detail;
  return {
    didSave: isSaveType,
    didShare: closeEvent.type === `saveAndShare`,
    event: closeEvent.type,
    ...values,
  };
}

export async function createEntityModalPromise(
  attrs: Partial<CreateEntityModalAttrs>,
  props: CreateEntityModalJsxProps['props'] = {},
): Promise<ModalReturn> {
  const closeEvent = await promisifyModal({
    tagName: `mp-create-entity-modal`,
    attrs,
    props,
    closeEvents: [`save`, `cancel`, `saveAndShare`],
  });

  return createEntityModalPromiseReturn(closeEvent);
}

export function promptManageSearchEntityModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
): Promise<ModalReturn> {
  return createEntityModalPromise({
    ...attrs,
    'modal-title': title,
  });
}

export function promptManageDashboardModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
  props: any = {},
): Promise<ModalReturn> {
  return createEntityModalPromise(
    {
      ...attrs,
      'entity-type': `dashboard`,
      'modal-title': title,
    },
    props,
  );
}

export function promptManageCohortModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
  props: any = {},
): Promise<ModalReturn> {
  return createEntityModalPromise(
    {
      ...attrs,
      'entity-type': `cohort`,
      'modal-title': title,
    },
    props,
  );
}

export function promptManageWorkspaceModal(
  title: string,
  attrs: Partial<CreateEntityModalAttrs> = {},
  projectId: number = null,
): Promise<ModalReturn> {
  if (projectId) {
    workspacesClient.getWorkspaces({projectId}).then((workspaces) => {
      const cem: CreateEntityModal = document.querySelector(`mp-create-entity-modal[entity-type=workspace]`);
      cem.existingEntityNames = workspaces.map((w) => w.name);
    });
  }

  // TODO I need to create a new API for accessing the element and promise to remove the need to query selector
  return createEntityModalPromise({
    ...attrs,
    'entity-type': `workspace`,
    'modal-title': title,
  });
}
